import { formatDistanceToNow, parseISO } from "date-fns";
import { X } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import type { CollectionJob } from "@/services/api/collection-jobs";
import {
  getStatusBadgeVariant,
  getStatusColor,
  calculateProgressPercentage,
  canCancel,
  shortenStatusDescription,
} from "./collectionJobsUtils";

interface CollectionJobsTableRowProps {
  job: CollectionJob;
  onCancel: (job: CollectionJob) => void;
  isCancelling?: boolean;
}

function PipelineProgress({
  value,
  total,
  statusDescription,
  color,
}: {
  value: number;
  total: number;
  statusDescription: string;
  color: string;
}) {
  const pct = calculateProgressPercentage(value, total, statusDescription);
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 min-w-[60px]">
        <div className="h-2 bg-grid-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${color} transition-all`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <span className="text-xs text-ink-600 whitespace-nowrap shrink-0">
        {value}/{total}
      </span>
    </div>
  );
}

function formatDate(dateString: string | null): string {
  if (!dateString) return "—";
  try {
    return formatDistanceToNow(parseISO(dateString), { addSuffix: true });
  } catch {
    return dateString;
  }
}

export function CollectionJobsTableRow({
  job,
  onCancel,
  isCancelling = false,
}: CollectionJobsTableRowProps) {
  return (
    <tr className="hover:bg-grid-50 transition-colors">
      <td className="px-6 py-4 align-top">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full shrink-0 ${getStatusColor(
                job.statusDescription
              )}`}
            />
            <Badge variant={getStatusBadgeVariant(job.statusDescription)}>
              {shortenStatusDescription(job.statusDescription)}
            </Badge>
          </div>
          {job.errorMessage && (
            <p className="text-xs text-red-600 leading-relaxed">
              {job.errorMessage}
            </p>
          )}
        </div>
      </td>
      <td className="px-6 py-4 align-top">
        <div
          className="text-sm text-ink-900 max-w-[250px] truncate"
          title={job.queryDescription ?? ""}
        >
          {job.queryDescription || "—"}
        </div>
      </td>
      <td className="px-6 py-4 align-top">
        <div className="text-sm text-ink-900 whitespace-nowrap">
          {formatDate(job.createdDate)}
        </div>
      </td>
      <td className="px-6 py-4 align-top">
        <div className="text-sm text-ink-900">{job.triggeredBy || "—"}</div>
      </td>
      <td className="px-6 py-4 align-top">
        {job.skipAnalysis ? (
          <span className="text-xs text-ink-400">—</span>
        ) : (
          <PipelineProgress
            value={job.processedProfiles}
            total={job.totalProfiles}
            statusDescription={job.statusDescription}
            color="bg-blue-500"
          />
        )}
      </td>
      <td className="px-6 py-4 align-top">
        <PipelineProgress
          value={job.classifiedProfiles}
          total={job.totalProfiles}
          statusDescription={job.statusDescription}
          color="bg-amber-500"
        />
      </td>
      <td className="px-6 py-4 align-top">
        {job.skipAnalysis ? (
          <span className="text-xs text-ink-400">—</span>
        ) : (
          <PipelineProgress
            value={job.analyzedProfiles}
            total={job.totalProfiles}
            statusDescription={job.statusDescription}
            color="bg-violet-500"
          />
        )}
      </td>
      <td className="px-6 py-4 align-top">
        <div className="text-sm text-ink-900 whitespace-nowrap">
          {formatDate(job.startedAt ?? "")}
        </div>
      </td>
      <td className="px-6 py-4 align-top">
        <div className="text-sm text-ink-900 whitespace-nowrap">
          {formatDate(job.completedAt ?? "")}
        </div>
      </td>
      <td className="px-6 py-4 align-top">
        <div className="text-sm text-ink-900 whitespace-nowrap">
          {job.remainingSearchCredits !== null &&
          job.remainingSearchCredits !== undefined
            ? job.remainingSearchCredits.toLocaleString()
            : "—"}
        </div>
      </td>
      <td className="px-6 py-4 align-top">
        <div className="text-sm text-ink-900 whitespace-nowrap">
          {job.remainingCollectCredits !== null &&
          job.remainingCollectCredits !== undefined
            ? job.remainingCollectCredits.toLocaleString()
            : "—"}
        </div>
      </td>
      <td className="px-6 py-4 align-top">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onCancel(job)}
            disabled={isCancelling || !canCancel(job.status)}
            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
            title={
              canCancel(job.status) ? "Cancel job" : "Job cannot be cancelled"
            }
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Cancel</span>
          </Button>
        </div>
      </td>
    </tr>
  );
}
